import { getRemainingTransactions } from './remainingTransactions';
import { COLUMNS, LINE_CONFIG, ROWS, SYMBOLS } from './slotConfig.js';
import { logRewardDebug } from './rewardDebug.js';

function randomItem(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function isFullLine(symbols, target) {
  return symbols.length === COLUMNS && symbols.every((symbol) => symbol === target);
}

const VISUAL_IDLE_SYMBOLS = [...SYMBOLS];

function isAnyFullLine(symbols) {
  return VISUAL_IDLE_SYMBOLS.some((symbol) => isFullLine(symbols, symbol));
}

export function randomNonWinningLine({ nearMiss = false } = {}) {
  let line;

  // Intelligent near-miss: occasionally build a line with five equal symbols
  // and one breaker. It preserves the visual tension without ever producing a
  // false full reward line. STAR is intentionally excluded from this pool.
  if (nearMiss && Math.random() < 0.62) {
    const base = randomItem(VISUAL_IDLE_SYMBOLS);
    let breaker = randomItem(VISUAL_IDLE_SYMBOLS.filter((symbol) => symbol !== base));
    if (!breaker) breaker = randomItem(VISUAL_IDLE_SYMBOLS);
    line = Array.from({ length: COLUMNS }, () => base);
    const breakerIndex = Math.floor(Math.random() * COLUMNS);
    line[breakerIndex] = breaker;
    return line;
  }

  do {
    line = Array.from({ length: COLUMNS }, () => randomItem(VISUAL_IDLE_SYMBOLS));
  } while (isAnyFullLine(line));
  return line;
}

function fullSymbolLine(symbol) {
  return Array.from({ length: COLUMNS }, () => symbol);
}

function receiverRevealSymbol({ usedSymbols = new Set(), previousSymbol = null } = {}) {
  // STAR is intentionally excluded from SYMBOLS and remains reserved for the
  // reward reveal. A newly active prize line can use any normal symbol, while
  // active simultaneous prize lines avoid repeating the same symbol. When a
  // line starts a fresh cycle, avoid reusing its own previous symbol if there
  // are alternatives; this makes consecutive current-receiver wins feel new.
  const primaryPool = SYMBOLS.filter((symbol) => !usedSymbols.has(symbol) && symbol !== previousSymbol);
  const fallbackPool = SYMBOLS.filter((symbol) => !usedSymbols.has(symbol));
  return randomItem(primaryPool.length ? primaryPool : (fallbackPool.length ? fallbackPool : SYMBOLS));
}

function lineRevealSymbol({ previousLine, usedSymbols, forceNew = false }) {
  if (
    !forceNew &&
    previousLine?.activeForWallet &&
    previousLine?.revealSymbol &&
    !usedSymbols.has(previousLine.revealSymbol)
  ) {
    return previousLine.revealSymbol;
  }

  return receiverRevealSymbol({
    usedSymbols,
    previousSymbol: forceNew ? previousLine?.revealSymbol : null,
  });
}

function normalizeAddress(address) {
  return String(address || '').toLowerCase();
}

function cycleIdForMilestone(transactionCounter, milestone) {
  const counter = Number(transactionCounter || 0);
  const safeMilestone = Number(milestone || 0);
  if (!safeMilestone || safeMilestone <= 0 || counter <= 0) return 0;
  return Math.floor(counter / safeMilestone);
}

export function snapshotToSlotState({ snapshot, walletAddress, previousState = null, revealOnInitialActive = false, initialRevealReason = null }) {
  const wallet = normalizeAddress(walletAddress);
  const usedRevealSymbols = new Set();

  const lines = Array.from({ length: ROWS }, (_, index) => {
    const config = LINE_CONFIG[index];
    const receiver = normalizeAddress(snapshot.currentReceivers?.[config.key]);
    const activeForWallet = Boolean(wallet && receiver === wallet);
    const previousLine = previousState?.lines?.[index];
    const wasActiveForWallet = Boolean(previousLine?.activeForWallet);
    const remaining = activeForWallet ? getRemainingTransactions(snapshot.transactionCounter, config.milestone) : 0;
    const cycleId = activeForWallet ? cycleIdForMilestone(snapshot.transactionCounter, config.milestone) : null;

    // If the same wallet immediately wins the next interval for the same line,
    // activeForWallet can stay true and there may be no clean inactive frame.
    // Detect the new prize by milestone cycle id instead of only comparing
    // remaining values, because remaining can be stale or skip frames during
    // swaps/polls. A new cycle must replay STAR reveal and pick a fresh symbol.
    const isNewPrizeCycle = Boolean(
      activeForWallet &&
      wasActiveForWallet &&
      previousLine?.cycleId != null &&
      cycleId !== previousLine.cycleId
    );

    const isInitialActiveReveal = Boolean(activeForWallet && !wasActiveForWallet && revealOnInitialActive);
    const shouldRevealToStar = Boolean(
      activeForWallet && (
        isNewPrizeCycle ||
        isInitialActiveReveal ||
        (previousState && !wasActiveForWallet)
      )
    );
    const revealSymbol = activeForWallet
      ? lineRevealSymbol({
          previousLine,
          usedSymbols: usedRevealSymbols,
          forceNew: isNewPrizeCycle || isInitialActiveReveal,
        })
      : receiverRevealSymbol();

    if (activeForWallet) usedRevealSymbols.add(revealSymbol);

    const nextLine = {
      index,
      key: config.key,
      milestone: config.milestone,
      reward: config.reward,
      activeForWallet,
      remaining,
      cycleId,
      revealToStar: shouldRevealToStar,
      revealSymbol,
      symbols: activeForWallet
        ? fullSymbolLine(revealSymbol)
        : previousLine?.activeForWallet
          ? randomNonWinningLine({ nearMiss: true })
          : previousLine?.symbols || randomNonWinningLine({ nearMiss: index < 3 }),
    };

    if (shouldRevealToStar) {
      logRewardDebug('line-reveal-start', {
        key: config.key,
        milestone: config.milestone,
        revealSymbol,
        remaining,
        previousRemaining: previousLine?.remaining || 0,
        previousCycleId: previousLine?.cycleId ?? null,
        cycleId,
        isNewPrizeCycle,
        isInitialActiveReveal,
        initialRevealReason,
        wallet,
        transactionCounter: snapshot.transactionCounter,
      });
    } else if (activeForWallet && wasActiveForWallet && previousLine?.revealSymbol !== revealSymbol) {
      logRewardDebug('line-symbol-reassigned', {
        key: config.key,
        previousSymbol: previousLine?.revealSymbol || null,
        revealSymbol,
        remaining,
        wallet,
        transactionCounter: snapshot.transactionCounter,
      });
    } else if (!activeForWallet && wasActiveForWallet) {
      logRewardDebug('line-ended', {
        key: config.key,
        previousSymbol: previousLine?.revealSymbol || null,
        previousRemaining: previousLine?.remaining || 0,
        wallet,
        transactionCounter: snapshot.transactionCounter,
      });
    }

    return nextLine;
  });

  preventFullVerticalSymbolColumns(lines);

  return {
    transactionCounter: snapshot.transactionCounter,
    lines,
  };
}

function preventFullVerticalSymbolColumns(lines) {
  for (let column = 0; column < COLUMNS; column += 1) {
    const first = lines[0]?.symbols?.[column];
    if (!first) continue;
    const isFullColumn = lines.every((line) => line?.symbols?.[column] === first);
    if (!isFullColumn) continue;

    const rowToBreak = Math.max(0, lines.findIndex((line) => !line.activeForWallet));
    const line = lines[rowToBreak];
    if (!line?.symbols) continue;
    const replacementPool = SYMBOLS.filter((symbol) => symbol !== first);
    line.symbols = [...line.symbols];
    line.symbols[column] = randomItem(replacementPool.length ? replacementPool : SYMBOLS);
    logRewardDebug('vertical-full-column-prevented', {
      column,
      rowToBreak,
      repeatedSymbol: first,
      replacementSymbol: line.symbols[column],
    });
  }
}

export function settleSpinResultLines(previousState, { preserveActiveReveals = true } = {}) {
  return {
    ...previousState,
    lines: previousState.lines.map((line) => {
      const revealSymbol = line.revealSymbol || receiverRevealSymbol();

      return {
        ...line,
        revealToStar: Boolean(line.activeForWallet && preserveActiveReveals && line.revealToStar),
        revealSymbol,
        symbols: line.activeForWallet ? fullSymbolLine(revealSymbol) : randomNonWinningLine({ nearMiss: true }),
      };
    }),
  };
}
